import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { getApiUrl } from '@/lib/utils';

const API = getApiUrl();

export const BankStatementsPage = ({ user, t, formatCurrency, buildingId = null }) => {
  const navigate = useNavigate();
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apartments, setApartments] = useState([]);
  
  // Upload & Analysis state
  const [uploading, setUploading] = useState(false);
  const [parseResults, setParseResults] = useState(null);
  const [currentStatementId, setCurrentStatementId] = useState(null);
  const [editedTransactions, setEditedTransactions] = useState({});
  
  // Posting state
  const [showPostConfirm, setShowPostConfirm] = useState(false);
  const [posting, setPosting] = useState(false);
  const [postResults, setPostResults] = useState(null);
  
  // Revert/Delete confirmation state
  const [showRevertConfirm, setShowRevertConfirm] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [reverting, setReverting] = useState(false);
  const [revertResults, setRevertResults] = useState(null);
  
  // Details modal state
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const getBuildingId = useCallback(() => {
    return buildingId || user?.building_id;
  }, [buildingId, user]);

  const fetchStatements = useCallback(async () => {
    try {
      const bid = getBuildingId();
      const url = bid ? `${API}/admin/bank-statements?building_id=${bid}` : `${API}/admin/bank-statements`;
      const response = await axios.get(url, { withCredentials: true });
      setStatements(response.data.statements || []);
    } catch (error) {
      console.error('Error fetching bank statements:', error);
      toast.error(t('loadDataFailed'));
    } finally {
      setLoading(false);
    }
  }, [getBuildingId, t]);

  const fetchApartments = useCallback(async () => {
    try {
      const bid = getBuildingId();
      const url = bid ? `${API}/apartments?building_id=${bid}` : `${API}/apartments`;
      const response = await axios.get(url, { withCredentials: true });
      setApartments(response.data.sort((a, b) => 
        (a.unit_number || '').localeCompare(b.unit_number || '', undefined, { numeric: true })
      ));
    } catch (error) {
      console.error('Error fetching apartments:', error);
    }
  }, [getBuildingId]);

  useEffect(() => {
    fetchStatements();
    fetchApartments();
  }, [fetchStatements, fetchApartments]);

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setParseResults(null);
    setCurrentStatementId(null);
    setEditedTransactions({});
    setPostResults(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const bid = getBuildingId();
      const url = bid 
        ? `${API}/admin/parse-bank-statement?building_id=${bid}`
        : `${API}/admin/parse-bank-statement`;

      const response = await axios.post(url, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setParseResults(response.data);
      setCurrentStatementId(response.data.bank_statement_id);
      setShowDetailsModal(true); // Open modal after upload
      
      // Refresh statements list
      fetchStatements();
      
      toast.success(t('bankStatementResults'));
    } catch (error) {
      console.error('Error parsing bank statement:', error);
      toast.error(error.response?.data?.detail || t('parseError'));
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  // Handle unit assignment change
  const handleUnitChange = (section, index, apartmentId) => {
    setEditedTransactions(prev => ({
      ...prev,
      [`${section}_${index}`]: apartmentId
    }));
  };

  // Get apartment ID for a transaction
  const getTransactionApartmentId = (section, index, originalApartmentNumber) => {
    const editKey = `${section}_${index}`;
    if (editedTransactions.hasOwnProperty(editKey)) {
      return editedTransactions[editKey];
    }
    if (originalApartmentNumber) {
      const apt = apartments.find(a => a.unit_number === originalApartmentNumber);
      return apt ? apt.id : '';
    }
    return '';
  };

  // Check if transaction was edited
  const isTransactionEdited = (section, index) => {
    return editedTransactions.hasOwnProperty(`${section}_${index}`);
  };

  // Get resident name for a transaction
  const getTransactionResidentName = (section, index, tx) => {
    const apartmentId = getTransactionApartmentId(section, index, tx.apartment_number);
    if (!apartmentId || apartmentId === 'pool') {
      return tx.resident_name || '-';
    }
    const apt = apartments.find(a => a.id === apartmentId);
    if (apt) {
      if (apt.residents && apt.residents.length > 1) {
        return apt.residents.map(r => r.name).join(', ');
      }
      return apt.resident_name || '-';
    }
    return tx.resident_name || '-';
  };

  // Render match type badge
  const renderMatchTypeBadge = (tx) => {
    const matchType = tx.match_type;
    const conflict = tx.conflict_info;
    
    if (!matchType && !conflict) return null;
    
    let badge = null;
    switch (matchType) {
      case 'unit_number':
        badge = <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded" title={t('matchedByUnitNumber') || 'Unit Number Match'}>🏠</span>;
        break;
      case 'full_name':
        badge = <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded" title={`${t('matchedByFullName') || 'Full Name Match'}: ${tx.matched_resident_name || ''}`}>👤</span>;
        break;
      case 'similar_name':
        badge = <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs rounded" title={`${t('matchedBySimilarName') || 'Similar Name'}: ${tx.matched_resident_name || ''}`}>≈</span>;
        break;
      case 'full_name_ambiguous':
      case 'similar_name_ambiguous':
        badge = <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 text-xs rounded" title={conflict || t('needsReview') || 'Needs Review'}>⚠️</span>;
        break;
      default:
        break;
    }
    
    const conflictBadge = conflict && matchType === 'unit_number' ? (
      <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-xs rounded ml-1" title={conflict}>⚡</span>
    ) : null;
    
    return (
      <div className="flex items-center gap-1">
        {badge}
        {conflictBadge}
      </div>
    );
  };

  // Handle posting to Operating Ledger
  const handlePost = async () => {
    if (!currentStatementId) return;
    
    setPosting(true);
    setPostResults(null);
    
    try {
      const bid = getBuildingId();
      
      const prepareTransactions = (transactions, section) => {
        return transactions.map((tx, idx) => ({
          ...tx,
          assigned_apartment_id: getTransactionApartmentId(section, idx, tx.apartment_number),
          section: section
        }));
      };
      
      const payload = {
        building_id: bid,
        bank_statement_id: currentStatementId,
        aidat_payments: prepareTransactions(parseResults?.aidat_payments || [], 'aidat'),
        other_payments: prepareTransactions(parseResults?.other_payments || [], 'other'),
        expenses: parseResults?.expenses || [],
        unidentified: prepareTransactions(parseResults?.unidentified || [], 'unidentified')
      };
      
      const response = await axios.post(
        `${API}/admin/post-bank-statement`,
        payload,
        { withCredentials: true }
      );
      
      setPostResults(response.data);
      setShowPostConfirm(false);
      
      if (response.data.success) {
        toast.success(t('bankStatementPosted'));
        fetchStatements();
      }
    } catch (error) {
      console.error('Error posting bank statement:', error);
      toast.error(error.response?.data?.detail || t('postingFailed'));
    } finally {
      setPosting(false);
    }
  };

  // Handle revert
  const handleRevert = async (statementId) => {
    setReverting(true);
    setRevertResults(null);
    
    try {
      const bid = getBuildingId();
      const url = bid 
        ? `${API}/admin/revert-bank-statement/${statementId}?building_id=${bid}`
        : `${API}/admin/revert-bank-statement/${statementId}`;
      
      const response = await axios.post(url, {}, { withCredentials: true });
      
      setRevertResults(response.data);
      setShowRevertConfirm(null);
      
      if (response.data.success) {
        toast.success(t('revertSuccess'));
        fetchStatements();
        // Clear current analysis if it was the reverted statement
        if (currentStatementId === statementId) {
          setParseResults(null);
          setCurrentStatementId(null);
        }
      }
    } catch (error) {
      console.error('Error reverting bank statement:', error);
      toast.error(error.response?.data?.detail || t('revertFailed'));
    } finally {
      setReverting(false);
    }
  };

  // Handle delete (only for uploaded, not posted)
  const handleDelete = async (statementId) => {
    try {
      const bid = getBuildingId();
      const url = bid 
        ? `${API}/admin/bank-statements/${statementId}?building_id=${bid}`
        : `${API}/admin/bank-statements/${statementId}`;
      
      await axios.delete(url, { withCredentials: true });
      
      toast.success(t('deleteSuccess'));
      setShowDeleteConfirm(null);
      fetchStatements();
      
      // Clear current analysis if it was the deleted statement
      if (currentStatementId === statementId) {
        setParseResults(null);
        setCurrentStatementId(null);
      }
    } catch (error) {
      console.error('Error deleting bank statement:', error);
      toast.error(error.response?.data?.detail || t('operationFailed'));
    }
  };

  // View statement details (load parse results)
  const handleViewDetails = async (statement) => {
    try {
      const bid = getBuildingId();
      const url = bid 
        ? `${API}/admin/bank-statements/${statement.id}?building_id=${bid}`
        : `${API}/admin/bank-statements/${statement.id}`;
      
      const response = await axios.get(url, { withCredentials: true });
      
      if (response.data.parse_results) {
        setParseResults({
          ...response.data.parse_results,
          summary: {
            aidat_count: response.data.aidat_count,
            aidat_total: response.data.aidat_total,
            other_payments_count: response.data.other_payments_count,
            other_payments_total: response.data.other_payments_total,
            expenses_count: response.data.expenses_count,
            expenses_total: response.data.expenses_total,
            unidentified_count: response.data.unidentified_count
          }
        });
        setCurrentStatementId(statement.id);
        setEditedTransactions({});
        setPostResults(null);
        setShowDetailsModal(true); // Open modal when details are loaded
      }
    } catch (error) {
      console.error('Error loading statement details:', error);
      toast.error(t('loadDataFailed'));
    }
  };

  // Close details modal
  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return dateStr;
    }
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('tr-TR');
    } catch {
      return dateStr;
    }
  };

  // Get current statement info
  const currentStatement = statements.find(s => s.id === currentStatementId);
  const isCurrentPosted = currentStatement?.status === 'posted';

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50 py-4 sm:py-8">
      <div className="container mx-auto px-2 sm:px-4">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/app')} 
                className="text-gray-600 hover:text-gray-900 flex items-center gap-1"
              >
                ← {t('back')}
              </button>
            </div>
            
            <h1 className="text-xl sm:text-2xl font-bold text-gray-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {t('bankStatementsTitle')}
            </h1>
            
            {/* Upload Button */}
            <label className="px-4 py-2 bg-purple-600 text-white rounded-full hover:bg-purple-700 transition-colors font-medium cursor-pointer">
              {uploading ? t('analyzing') : t('uploadNewStatement')}
              <input
                type="file"
                accept=".xls,.xlsx"
                onChange={handleFileUpload}
                className="hidden"
                disabled={uploading}
                data-testid="bank-statement-upload-input"
              />
            </label>
          </div>
        </div>

        {/* Statements List */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            {t('uploadedStatements')}
          </h2>
          
          {statements.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {t('noStatementsYet')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-2 font-semibold">{t('statementFilename')}</th>
                    <th className="text-left py-3 px-2 font-semibold">{t('statementUploadDate')}</th>
                    <th className="text-left py-3 px-2 font-semibold">{t('statementStatus')}</th>
                    <th className="text-right py-3 px-2 font-semibold">{t('aidatPayments')}</th>
                    <th className="text-right py-3 px-2 font-semibold">{t('expensesSection')}</th>
                    <th className="text-center py-3 px-2 font-semibold">{t('actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {statements.map((stmt) => (
                    <tr 
                      key={stmt.id} 
                      className={`border-b border-gray-100 hover:bg-gray-50 ${currentStatementId === stmt.id ? 'bg-purple-50' : ''}`}
                    >
                      <td className="py-3 px-2 font-medium">{stmt.filename}</td>
                      <td className="py-3 px-2 text-gray-600">{formatDate(stmt.upload_date)}</td>
                      <td className="py-3 px-2">
                        {stmt.status === 'posted' ? (
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                            {t('statementPosted')}
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full font-medium">
                            {t('statementUploaded')}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-2 text-right">
                        {stmt.aidat_count} ({formatCurrency(stmt.aidat_total || 0)})
                      </td>
                      <td className="py-3 px-2 text-right">
                        {stmt.expenses_count} ({formatCurrency(Math.abs(stmt.expenses_total || 0))})
                      </td>
                      <td className="py-3 px-2 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleViewDetails(stmt)}
                            className="px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 text-xs rounded transition-colors"
                            data-testid={`view-details-${stmt.id}`}
                          >
                            {t('viewDetails')}
                          </button>
                          {stmt.status === 'posted' ? (
                            <button
                              onClick={() => setShowRevertConfirm(stmt)}
                              className="px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 text-xs rounded transition-colors"
                              data-testid={`revert-${stmt.id}`}
                            >
                              {t('revertStatement')}
                            </button>
                          ) : (
                            <button
                              onClick={() => setShowDeleteConfirm(stmt)}
                              className="px-2 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs rounded transition-colors"
                              data-testid={`delete-${stmt.id}`}
                            >
                              {t('deleteStatement')}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Statement Details Modal */}
        {showDetailsModal && parseResults && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="bank-statement-details-modal">
            <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 p-4 sm:p-6 flex items-center justify-between rounded-t-2xl z-10">
                <div className="flex items-center gap-4">
                  <h2 className="text-lg font-bold text-gray-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {t('bankStatementResults')}
                    {currentStatement && (
                      <span className="ml-2 text-sm font-normal text-gray-500">
                        ({currentStatement.filename})
                      </span>
                    )}
                  </h2>
                  {isCurrentPosted && (
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                      {t('statementPosted')}
                    </span>
                  )}
                </div>
                <button
                  onClick={handleCloseDetailsModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-2"
                  data-testid="close-details-modal"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-4 sm:p-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                    <div className="text-sm text-green-600 font-medium">{t('aidatPayments')}</div>
                    <div className="text-xl font-bold text-green-700">{parseResults.summary?.aidat_count || 0}</div>
                    <div className="text-sm text-green-600">{formatCurrency(parseResults.summary?.aidat_total || 0)}</div>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                    <div className="text-sm text-blue-600 font-medium">{t('otherPaymentsSection')}</div>
                    <div className="text-xl font-bold text-blue-700">{parseResults.summary?.other_payments_count || 0}</div>
                    <div className="text-sm text-blue-600">{formatCurrency(parseResults.summary?.other_payments_total || 0)}</div>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <div className="text-sm text-red-600 font-medium">{t('expensesSection')}</div>
                    <div className="text-xl font-bold text-red-700">{parseResults.summary?.expenses_count || 0}</div>
                    <div className="text-sm text-red-600">{formatCurrency(Math.abs(parseResults.summary?.expenses_total || 0))}</div>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="text-sm text-amber-600 font-medium">{t('unidentifiedTransactions')}</div>
                    <div className="text-xl font-bold text-amber-700">{parseResults.summary?.unidentified_count || 0}</div>
                  </div>
                </div>

                {/* Aidat Payments Table */}
                {parseResults.aidat_payments?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-md font-semibold text-gray-700 mb-3">{t('aidatPayments')}</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border border-gray-200 rounded-lg">
                        <thead className="bg-green-50">
                          <tr>
                            <th className="px-3 py-2 text-left">🎯</th>
                            <th className="px-3 py-2 text-left">{t('date')}</th>
                            <th className="px-3 py-2 text-left">{t('assignUnit')}</th>
                            <th className="px-3 py-2 text-left">{t('residentName')}</th>
                            <th className="px-3 py-2 text-right">{t('amount')}</th>
                            <th className="px-3 py-2 text-left">{t('description')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {parseResults.aidat_payments.map((tx, idx) => (
                            <tr 
                              key={idx} 
                              className={`border-t border-gray-100 ${isTransactionEdited('aidat', idx) ? 'bg-yellow-50' : ''} ${tx.conflict_info ? 'bg-red-50' : ''}`}
                            >
                              <td className="px-3 py-2">{renderMatchTypeBadge(tx)}</td>
                              <td className="px-3 py-2">{formatShortDate(tx.date)}</td>
                              <td className="px-3 py-2">
                                <select
                                  value={getTransactionApartmentId('aidat', idx, tx.apartment_number)}
                                  onChange={(e) => handleUnitChange('aidat', idx, e.target.value)}
                                  className="w-full px-2 py-1 border rounded text-sm"
                                  disabled={isCurrentPosted}
                                >
                                  <option value="">{t('selectUnit')}</option>
                                  <option value="pool">{t('poolAccount')}</option>
                                  {apartments.map(apt => (
                                    <option key={apt.id} value={apt.id}>{apt.unit_number}</option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-3 py-2 text-gray-600">{getTransactionResidentName('aidat', idx, tx)}</td>
                              <td className="px-3 py-2 text-right font-medium text-green-600">{formatCurrency(tx.amount)}</td>
                              <td className="px-3 py-2 text-gray-600 max-w-xs truncate" title={tx.description}>{tx.description}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Other Payments Table */}
                {parseResults.other_payments?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-md font-semibold text-gray-700 mb-3">{t('otherPaymentsSection')}</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border border-gray-200 rounded-lg">
                        <thead className="bg-blue-50">
                          <tr>
                            <th className="px-3 py-2 text-left">🎯</th>
                            <th className="px-3 py-2 text-left">{t('date')}</th>
                            <th className="px-3 py-2 text-left">{t('assignUnit')}</th>
                            <th className="px-3 py-2 text-left">{t('residentName')}</th>
                            <th className="px-3 py-2 text-right">{t('amount')}</th>
                            <th className="px-3 py-2 text-left">{t('description')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {parseResults.other_payments.map((tx, idx) => (
                            <tr 
                              key={idx} 
                              className={`border-t border-gray-100 ${isTransactionEdited('other', idx) ? 'bg-yellow-50' : ''}`}
                            >
                              <td className="px-3 py-2">{renderMatchTypeBadge(tx)}</td>
                              <td className="px-3 py-2">{formatShortDate(tx.date)}</td>
                              <td className="px-3 py-2">
                                <select
                                  value={getTransactionApartmentId('other', idx, tx.apartment_number)}
                                  onChange={(e) => handleUnitChange('other', idx, e.target.value)}
                                  className="w-full px-2 py-1 border rounded text-sm"
                                  disabled={isCurrentPosted}
                                >
                                  <option value="">{t('selectUnit')}</option>
                                  <option value="pool">{t('poolAccount')}</option>
                                  {apartments.map(apt => (
                                    <option key={apt.id} value={apt.id}>{apt.unit_number}</option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-3 py-2 text-gray-600">{getTransactionResidentName('other', idx, tx)}</td>
                              <td className="px-3 py-2 text-right font-medium text-blue-600">{formatCurrency(tx.amount)}</td>
                              <td className="px-3 py-2 text-gray-600 max-w-xs truncate" title={tx.description}>{tx.description}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Expenses Table */}
                {parseResults.expenses?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-md font-semibold text-gray-700 mb-3">{t('expensesSection')}</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border border-gray-200 rounded-lg">
                        <thead className="bg-red-50">
                          <tr>
                            <th className="px-3 py-2 text-left">{t('date')}</th>
                            <th className="px-3 py-2 text-left">{t('category')}</th>
                            <th className="px-3 py-2 text-right">{t('amount')}</th>
                            <th className="px-3 py-2 text-left">{t('description')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {parseResults.expenses.map((tx, idx) => (
                            <tr key={idx} className="border-t border-gray-100">
                              <td className="px-3 py-2">{formatShortDate(tx.date)}</td>
                              <td className="px-3 py-2">
                                <span className="text-gray-700">{t(`expenseCategories.${tx.category}`) || tx.category}</span>
                                {tx.subcategory && (
                                  <span className="text-gray-500 text-xs ml-1">
                                    / {t(`expenseSubcategories.${tx.category}.${tx.subcategory}`) || tx.subcategory}
                                  </span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-right font-medium text-red-600">{formatCurrency(Math.abs(tx.amount))}</td>
                              <td className="px-3 py-2 text-gray-600 max-w-xs truncate" title={tx.description}>{tx.description}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Unidentified Transactions Table */}
                {parseResults.unidentified?.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-md font-semibold text-gray-700 mb-3">{t('unidentifiedTransactions')}</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border border-gray-200 rounded-lg">
                        <thead className="bg-amber-50">
                          <tr>
                            <th className="px-3 py-2 text-left">🎯</th>
                            <th className="px-3 py-2 text-left">{t('date')}</th>
                            <th className="px-3 py-2 text-left">{t('assignUnit')}</th>
                            <th className="px-3 py-2 text-left">{t('residentName')}</th>
                            <th className="px-3 py-2 text-right">{t('amount')}</th>
                            <th className="px-3 py-2 text-left">{t('description')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {parseResults.unidentified.map((tx, idx) => (
                            <tr 
                              key={idx} 
                              className={`border-t border-gray-100 ${isTransactionEdited('unidentified', idx) ? 'bg-yellow-50' : ''}`}
                            >
                              <td className="px-3 py-2">{renderMatchTypeBadge(tx)}</td>
                              <td className="px-3 py-2">{formatShortDate(tx.date)}</td>
                              <td className="px-3 py-2">
                                <select
                                  value={getTransactionApartmentId('unidentified', idx, tx.apartment_number)}
                                  onChange={(e) => handleUnitChange('unidentified', idx, e.target.value)}
                                  className="w-full px-2 py-1 border rounded text-sm"
                                  disabled={isCurrentPosted}
                                >
                                  <option value="">{t('selectUnit')}</option>
                                  <option value="pool">{t('poolAccount')}</option>
                                  {apartments.map(apt => (
                                    <option key={apt.id} value={apt.id}>{apt.unit_number}</option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-3 py-2 text-gray-600">{getTransactionResidentName('unidentified', idx, tx)}</td>
                              <td className="px-3 py-2 text-right font-medium text-amber-600">{formatCurrency(tx.amount)}</td>
                              <td className="px-3 py-2 text-gray-600 max-w-xs truncate" title={tx.description}>{tx.description}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Post Button (only for non-posted statements) */}
                {!isCurrentPosted && (
                  <div className="flex justify-end mt-6">
                    <button
                      onClick={() => setShowPostConfirm(true)}
                      className="px-6 py-3 bg-teal-600 text-white rounded-full hover:bg-teal-700 transition-colors font-semibold"
                      data-testid="post-to-ledger-btn"
                    >
                      {t('postToOperatingLedger')}
                    </button>
                  </div>
                )}

                {/* Post Results */}
                {postResults && (
                  <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-xl">
                    <h4 className="font-semibold text-green-700 mb-2">{t('postingComplete')}</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">{t('transactionsProcessed')}:</span>
                        <span className="font-semibold ml-2">{postResults.processed_count}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">{t('assignedToUnits')}:</span>
                        <span className="font-semibold ml-2">{postResults.unit_payments}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">{t('assignedToPool')}:</span>
                        <span className="font-semibold ml-2">{postResults.pool_payments}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">{t('expensesPosted')}:</span>
                        <span className="font-semibold ml-2">{postResults.expenses_posted}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Revert Results Display */}
        {revertResults && (
          <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
            <h3 className="font-semibold text-gray-700 mb-3">{t('revertSuccess')}</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="bg-red-50 p-3 rounded-lg">
                <span className="text-gray-600">{t('expensesRemoved')}:</span>
                <span className="font-semibold ml-2 text-red-700">{revertResults.expenses_removed}</span>
              </div>
              <div className="bg-blue-50 p-3 rounded-lg">
                <span className="text-gray-600">{t('paymentsRemoved')}:</span>
                <span className="font-semibold ml-2 text-blue-700">{revertResults.maintenance_payments_removed + revertResults.other_payments_removed}</span>
              </div>
              <div className="bg-purple-50 p-3 rounded-lg">
                <span className="text-gray-600">{t('poolEntriesRemoved')}:</span>
                <span className="font-semibold ml-2 text-purple-700">{revertResults.pool_entries_removed}</span>
              </div>
              <div className="bg-amber-50 p-3 rounded-lg">
                <span className="text-gray-600">{t('feesRecalculated')}:</span>
                <span className="font-semibold ml-2 text-amber-700">{revertResults.fees_recalculated}</span>
              </div>
            </div>
          </div>
        )}

        {/* Post Confirmation Modal */}
        {showPostConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-md p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">{t('confirmPostTitle')}</h3>
              <p className="text-gray-600 mb-6">{t('confirmPostMessage')}</p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowPostConfirm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={posting}
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={handlePost}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                  disabled={posting}
                >
                  {posting ? t('posting') : t('confirm')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Revert Confirmation Modal */}
        {showRevertConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-md p-6">
              <h3 className="text-lg font-bold text-red-700 mb-4">{t('revertConfirmTitle')}</h3>
              <p className="text-gray-600 mb-2">
                <span className="font-semibold">{t('statementFilename')}:</span> {showRevertConfirm.filename}
              </p>
              <p className="text-gray-600 mb-6">{t('revertConfirmMessage')}</p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowRevertConfirm(null)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={reverting}
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={() => handleRevert(showRevertConfirm.id)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  disabled={reverting}
                >
                  {reverting ? t('revertInProgress') : t('revertStatement')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-md p-6">
              <h3 className="text-lg font-bold text-gray-800 mb-4">{t('deleteConfirmTitle')}</h3>
              <p className="text-gray-600 mb-2">
                <span className="font-semibold">{t('statementFilename')}:</span> {showDeleteConfirm.filename}
              </p>
              <p className="text-gray-600 mb-6">{t('deleteConfirmMessage')}</p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={() => handleDelete(showDeleteConfirm.id)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  {t('deleteStatement')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BankStatementsPage;
